import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
    LinearProgress,
    Alert,
    List,
    ListItem,
    IconButton,
    Divider,
    alpha,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import {
    scrapeTrackEvents,
    scrapeAthleticNetMeet,
    getScrapeJobs,
    checkDuplicate,
    getTrackEventJobStatus,
    getScrapedSchools,
    matchScrapedData,
} from 'api/TrackEvents';
import { usePusher } from 'contexts/PusherContext';
import { useSelector } from 'react-redux';
import useTheme from '@mui/system/useTheme';
import TrackEventMatchReview from './TrackEventMatchReview';
import TrackEventSchoolSelector from './TrackEventSchoolSelector';
import { userSelector } from 'redux/reducers/user';

type ScraperSource = 'milesplit' | 'athletic_net_meet';

interface TrackEventScraperDialogProps {
    open: boolean;
    onClose: () => void;
    group: any;
    onSuccess?: () => void;
    onViewResults?: () => void;
    defaultSource?: ScraperSource;
}

const detectSource = (url: string): ScraperSource | null => {
    if (url.includes('milesplit.com')) return 'milesplit';
    if (url.includes('athletic.net') && url.includes('/meet/')) return 'athletic_net_meet';
    return null;
};

const TrackEventScraperDialog: React.FC<TrackEventScraperDialogProps> = ({
    open,
    onClose,
    group,
    onSuccess,
    onViewResults,
    defaultSource,
}) => {
    const pusher = usePusher();
    const { userData } = useSelector(userSelector);
    const theme = useTheme();

    const glassStyle = {
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    };

    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [jobId, setJobId] = useState<string | null>(null);
    const [status, setStatus] = useState<string>('');
    const [statusMessage, setStatusMessage] = useState('');
    const [results, setResults] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [urlError, setUrlError] = useState<string | null>(null);

    // Import history
    const [importHistory, setImportHistory] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Duplicate detection
    const [duplicateCheck, setDuplicateCheck] = useState<any>(null);
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);

    // School selection
    const [showSchoolSelector, setShowSchoolSelector] = useState(false);
    const [scrapedSchools, setScrapedSchools] = useState<string[]>([]);
    const [organizationName, setOrganizationName] = useState<string>('');

    // Match review
    const [showMatchReview, setShowMatchReview] = useState(false);

    const loadImportHistory = useCallback(async () => {
        if (!group?.uuid) return;
        try {
            setHistoryLoading(true);
            const response = await getScrapeJobs(group.uuid, 50); // Increased from 10 to 50
            setImportHistory(response.data.jobs || []);
        } catch (err) {
            console.error('Failed to load import history:', err);
        } finally {
            setHistoryLoading(false);
        }
    }, [group?.uuid]);

    // Load import history when dialog opens
    useEffect(() => {
        if (open) {
            loadImportHistory();
        }
    }, [open, loadImportHistory]);

    // Check for duplicate URL when URL changes
    useEffect(() => {
        if (!url || !group?.uuid) {
            setDuplicateCheck(null);
            return;
        }

        // Debounce the check - only for MileSplit (which uses the scrape jobs table)
        const timer = setTimeout(async () => {
            const source = detectSource(url);
            if (source === 'milesplit') {
                try {
                    setCheckingDuplicate(true);
                    const response = await checkDuplicate({
                        url,
                        group_uuid: group.uuid,
                    });
                    setDuplicateCheck(response.data);
                } catch (err) {
                    console.error('Duplicate check failed:', err);
                } finally {
                    setCheckingDuplicate(false);
                }
            } else {
                setDuplicateCheck(null);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [url, group]);

    // Pusher event handlers
    const handleScrapingStarted = useCallback((data: any) => {
        setStatus('processing');
        setStatusMessage(data.message || 'Fetching meet data...');
    }, []);

    const handleScrapingMatching = useCallback((data: any) => {
        setStatus('matching');
        setStatusMessage(data.message || 'Matching athletes...');
    }, []);

    const handleScrapingComplete = useCallback(
        async (data: any) => {
            setStatus('complete');
            setStatusMessage('Scraping complete! Select schools to match.');
            setLoading(false);

            // Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('Track Meet Scraping Complete', {
                    body: `Ready to select schools from ${
                        data.results?.meet_info?.meet_name || 'meet'
                    }`,
                    icon: '/logo192.png',
                });
            }

            // Refresh history
            loadImportHistory();

            // Fetch schools and show school selector
            // Use data.job_id from event or fall back to jobId state
            const targetJobId = data.job_id || jobId;

            if (!targetJobId) {
                console.error('No job ID available');
                setError('No job ID available for fetching schools');
                return;
            }

            try {
                const response = await getScrapedSchools(targetJobId);
                setScrapedSchools(response.data.schools);
                setOrganizationName(response.data.organization_name || '');
                setTimeout(() => {
                    setShowSchoolSelector(true);
                }, 500);
            } catch (error) {
                console.error('Failed to fetch schools:', error);
                setError('Failed to load schools from scraped data');
            }
        },
        [loadImportHistory, jobId],
    );

    const handleScrapingFailed = useCallback((data: any) => {
        setStatus('failed');
        setError(data.error || 'Import failed');
        setLoading(false);

        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Track Meet Import Failed', {
                body: data.error || 'An error occurred',
                icon: '/logo192.png',
            });
        }
    }, []);

    // Subscribe to Pusher events
    useEffect(() => {
        if (!pusher || !userData || !jobId) return;

        const channel = pusher.subscribe(`private-users.${userData.uuid}`);

        channel.bind('track-event.scraping.started', handleScrapingStarted);
        channel.bind('track-event.scraping.matching', handleScrapingMatching);
        channel.bind('track-event.scraping.complete', handleScrapingComplete);
        channel.bind('track-event.scraping.failed', handleScrapingFailed);

        return () => {
            channel.unbind('track-event.scraping.started', handleScrapingStarted);
            channel.unbind('track-event.scraping.matching', handleScrapingMatching);
            channel.unbind('track-event.scraping.complete', handleScrapingComplete);
            channel.unbind('track-event.scraping.failed', handleScrapingFailed);
            pusher.unsubscribe(`private-users.${userData.uuid}`);
        };
    }, [
        pusher,
        userData,
        jobId,
        handleScrapingStarted,
        handleScrapingMatching,
        handleScrapingComplete,
        handleScrapingFailed,
    ]);

    // Polling fallback when Pusher isn't configured (local development)
    useEffect(() => {
        if (!jobId || status === 'complete' || status === 'failed') return;

        const pollInterval = setInterval(async () => {
            try {
                const response = await getTrackEventJobStatus(jobId);
                const jobData = response.data;
                if (jobData.status === 'complete') {
                    handleScrapingComplete({
                        job_id: jobId,
                        status: 'complete',
                        results: {
                            meet_info: {
                                meet_name: jobData.meet_name,
                            },
                            total_results: jobData.total_results,
                        },
                    });
                    clearInterval(pollInterval);
                } else if (jobData.status === 'failed') {
                    handleScrapingFailed({
                        job_id: jobId,
                        status: 'failed',
                        error: jobData.error || 'Unknown error',
                    });
                    clearInterval(pollInterval);
                }
            } catch (error) {
                console.error('Error polling job status:', error);
            }
        }, 3000); // Poll every 3 seconds

        return () => clearInterval(pollInterval);
    }, [jobId, status, handleScrapingComplete, handleScrapingFailed]);

    // Request notification permission when dialog opens
    useEffect(() => {
        if (open && 'Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, [open]);

    const activeSource = detectSource(url) || defaultSource || 'milesplit';

    const validateUrl = (urlToCheck: string): boolean => {
        if (!urlToCheck) {
            setUrlError('Please enter a URL');
            return false;
        }
        const source = detectSource(urlToCheck);
        if (!source) {
            setUrlError('URL must be from milesplit.com or athletic.net (meet page)');
            return false;
        }
        if (source === 'milesplit') {
            if (!urlToCheck.includes('/results/') || !urlToCheck.includes('type=raw')) {
                setUrlError('Please use the raw results URL (must include /results/ and type=raw)');
                return false;
            }
        }
        if (source === 'athletic_net_meet') {
            if (!urlToCheck.includes('/meet/')) {
                setUrlError(
                    'Please use an Athletic.net meet results URL (e.g. athletic.net/TrackAndField/meet/123456/results/all)',
                );
                return false;
            }
        }
        setUrlError(null);
        return true;
    };

    const handleStartScrape = async () => {
        if (!validateUrl(url)) return;

        const source = detectSource(url);
        try {
            setLoading(true);
            setError(null);
            setStatus('pending');
            setStatusMessage('Queueing import job...');

            let response;
            if (source === 'athletic_net_meet') {
                response = await scrapeAthleticNetMeet({
                    url,
                    group_uuid: group.uuid,
                });
            } else {
                response = await scrapeTrackEvents({
                    url,
                    group_uuid: group.uuid,
                });
            }

            setJobId(response.data.job_id);
            setStatus(response.data.status);
            setStatusMessage(response.data.message);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to start import');
            setLoading(false);
        }
    };

    const handleProceedWithDuplicate = () => {
        setDuplicateCheck(null);
        handleStartScrape();
    };

    const handleViewMatches = () => {
        setShowMatchReview(true);
    };

    const handleMatchReviewClose = () => {
        setShowMatchReview(false);
        setResults(null);
        setStatus('');
        setUrl('');
        setJobId(null);
        if (onSuccess) onSuccess();
    };

    const handleSchoolSelectionConfirm = useCallback(
        async (selectedSchools: string[]) => {
            setShowSchoolSelector(false);
            setLoading(true);
            setStatus('matching');
            setStatusMessage('Matching athletes to group members...');

            try {
                const response = await matchScrapedData({
                    job_id: jobId!,
                    schools: selectedSchools,
                });

                setResults(response.data.results);
                setLoading(false);
                setStatus('complete');
                setStatusMessage('Matching complete! Please review and confirm to save data.');

                // Show match review dialog
                setTimeout(() => {
                    setShowMatchReview(true);
                }, 300);
            } catch (error) {
                console.error('Matching failed:', error);
                setLoading(false);
                setStatus('failed');
                setError('Failed to match athletes');
            }
        },
        [jobId],
    );

    const handleSchoolSelectionCancel = () => {
        setShowSchoolSelector(false);
        setStatus('');
        setUrl('');
        setJobId(null);
    };

    const handleOpenSchoolSelector = useCallback(async () => {
        if (!jobId) {
            console.error('No job ID available');
            setError('No job ID available');
            return;
        }

        try {
            const response = await getScrapedSchools(jobId);
            setScrapedSchools(response.data.schools);
            setOrganizationName(response.data.organization_name || '');
            setShowSchoolSelector(true);
        } catch (error) {
            console.error('Failed to fetch schools:', error);
            setError('Failed to load schools from scraped data');
        }
    }, [jobId]);

    const handleClose = () => {
        if (!loading) {
            setUrl('');
            setError(null);
            setUrlError(null);
            setStatus('');
            setResults(null);
            setJobId(null);
            setDuplicateCheck(null);
            onClose();
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: glassStyle,
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            {activeSource === 'athletic_net_meet' ? (
                                <img
                                    src="/athletic-net-logo.png"
                                    alt="Athletic.net"
                                    style={{ height: 24 }}
                                />
                            ) : (
                                <img
                                    src="/milesplit-logo.png"
                                    alt="MileSplit"
                                    style={{ height: 24 }}
                                />
                            )}
                            <Typography variant="h6">Meet Scraper</Typography>
                        </Box>
                        <IconButton onClick={handleClose} disabled={loading}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {/* URL Input Section */}
                    <Box mb={3}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {activeSource === 'athletic_net_meet'
                                ? 'Enter the Athletic.net meet results URL for the meet you want to import.'
                                : 'Enter the MileSplit raw results URL for the meet you want to import.'}
                        </Typography>
                        <TextField
                            fullWidth
                            label={
                                activeSource === 'athletic_net_meet'
                                    ? 'Athletic.net Meet URL'
                                    : 'MileSplit URL'
                            }
                            placeholder={
                                activeSource === 'athletic_net_meet'
                                    ? 'https://www.athletic.net/TrackAndField/meet/123456/results/all'
                                    : 'https://oh.milesplit.com/meets/.../results/...?type=raw'
                            }
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            disabled={loading}
                            error={!!urlError}
                            helperText={urlError}
                            margin="normal"
                        />
                        {checkingDuplicate && (
                            <Box mt={1} display="flex" alignItems="center">
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                <Typography variant="caption">
                                    Checking for duplicates...
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Duplicate Warning */}
                    {duplicateCheck?.is_duplicate && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <Typography variant="body2" gutterBottom>
                                This meet was already imported on{' '}
                                {new Date(
                                    duplicateCheck.existing_import.imported_at,
                                ).toLocaleDateString()}{' '}
                                by {duplicateCheck.existing_import.imported_by}.
                            </Typography>
                            <Typography variant="body2">
                                {duplicateCheck.existing_import.total_results} results were
                                imported. Do you want to import it again?
                            </Typography>
                        </Alert>
                    )}

                    {/* Status Display */}
                    {status && (
                        <Box mb={3}>
                            {loading && <LinearProgress sx={{ mb: 2 }} />}

                            {status === 'complete' && (
                                <Alert severity="warning" icon={<CheckCircleIcon />}>
                                    <Typography variant="body2" gutterBottom>
                                        {statusMessage}
                                    </Typography>
                                    {(!results || !results.matched) && (
                                        <Box mt={2}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="large"
                                                onClick={handleOpenSchoolSelector}
                                                fullWidth
                                            >
                                                Select Schools to Match
                                            </Button>
                                        </Box>
                                    )}
                                    {results && results.matched && (
                                        <Box mt={1}>
                                            <Typography variant="body2">
                                                <strong>Meet:</strong>{' '}
                                                {results.meet_info?.meet_name || 'Unknown'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Total Results:</strong>{' '}
                                                {results.total_results}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Matched:</strong>{' '}
                                                {results.matched?.length || 0} athletes
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Unmatched:</strong>{' '}
                                                {results.unmatched?.length || 0}
                                            </Typography>
                                            {results.multiple_matches?.length > 0 && (
                                                <Typography variant="body2">
                                                    <strong>Needs Review:</strong>{' '}
                                                    {results.multiple_matches.length} ambiguous
                                                    matches
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Alert>
                            )}

                            {status === 'failed' && error && (
                                <Alert severity="error" icon={<ErrorIcon />}>
                                    <Typography variant="body2">{error}</Typography>
                                </Alert>
                            )}

                            {(status === 'pending' ||
                                status === 'processing' ||
                                status === 'matching') && (
                                <Alert severity="info">
                                    <Typography variant="body2">{statusMessage}</Typography>
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Import History - Unique URLs */}
                    <Box>
                        {historyLoading ? (
                            <CircularProgress size={24} />
                        ) : importHistory.length === 0 ? (
                            <>
                                <Typography variant="subtitle2" gutterBottom>
                                    {userData?.first_name}, you have imported 0 events
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    No previous imports
                                </Typography>
                            </>
                        ) : (
                            <List dense>
                                {(() => {
                                    // Group by unique URL and get most recent import for each
                                    const uniqueUrls = importHistory.reduce((acc, job) => {
                                        if (!job.url) return acc;
                                        if (
                                            !acc[job.url] ||
                                            new Date(job.created_at) >
                                                new Date(acc[job.url].created_at)
                                        ) {
                                            acc[job.url] = job;
                                        }
                                        return acc;
                                    }, {} as Record<string, any>);

                                    const uniqueCount = Object.keys(uniqueUrls).length;

                                    return (
                                        <>
                                            <Typography
                                                variant="subtitle2"
                                                gutterBottom
                                                sx={{ mb: 2 }}
                                            >
                                                {userData?.first_name}, you have imported{' '}
                                                {uniqueCount}{' '}
                                                {uniqueCount === 1 ? 'event' : 'events'}
                                            </Typography>
                                            {Object.values(uniqueUrls).map(
                                                (job: any, index: number) => (
                                                    <React.Fragment key={job.job_id}>
                                                        <ListItem
                                                            sx={{
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    width: '100%',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="600"
                                                                    >
                                                                        {job.meet_name ||
                                                                            'Unknown Meet'}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                        display="block"
                                                                    >
                                                                        {new Date(
                                                                            job.created_at,
                                                                        ).toLocaleDateString()}{' '}
                                                                        • {job.total_results}{' '}
                                                                        results
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                        display="block"
                                                                        sx={{
                                                                            overflow: 'hidden',
                                                                            textOverflow:
                                                                                'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            maxWidth: '400px',
                                                                        }}
                                                                    >
                                                                        {job.url}
                                                                    </Typography>
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        gap: 1,
                                                                        ml: 2,
                                                                    }}
                                                                >
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={() =>
                                                                            setUrl(job.url)
                                                                        }
                                                                        disabled={loading}
                                                                    >
                                                                        Re-import
                                                                    </Button>
                                                                    {job.status === 'complete' &&
                                                                        onViewResults && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                onClick={() => {
                                                                                    onClose();
                                                                                    onViewResults();
                                                                                }}
                                                                            >
                                                                                View
                                                                            </Button>
                                                                        )}
                                                                </Box>
                                                            </Box>
                                                        </ListItem>
                                                        {index <
                                                            Object.values(uniqueUrls).length -
                                                                1 && <Divider />}
                                                    </React.Fragment>
                                                ),
                                            )}
                                        </>
                                    );
                                })()}
                            </List>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    {status === 'complete' && results && (
                        <Button onClick={handleViewMatches} variant="contained" color="primary">
                            Review & Import
                        </Button>
                    )}
                    {status !== 'complete' && (
                        <Button
                            onClick={
                                duplicateCheck?.is_duplicate
                                    ? handleProceedWithDuplicate
                                    : handleStartScrape
                            }
                            variant="contained"
                            disabled={loading || !url || !!urlError}
                            color={duplicateCheck?.is_duplicate ? 'warning' : 'primary'}
                        >
                            {loading
                                ? 'Importing...'
                                : duplicateCheck?.is_duplicate
                                ? 'Import Anyway'
                                : 'Start Import'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* School Selector Dialog */}
            {showSchoolSelector && (
                <TrackEventSchoolSelector
                    open={showSchoolSelector}
                    schools={scrapedSchools}
                    organizationName={organizationName}
                    onConfirm={handleSchoolSelectionConfirm}
                    onCancel={handleSchoolSelectionCancel}
                />
            )}

            {/* Match Review Dialog */}
            {showMatchReview && results && (
                <TrackEventMatchReview
                    open={showMatchReview}
                    onClose={handleMatchReviewClose}
                    results={results}
                    group={group}
                    importUrl={url}
                />
            )}
        </>
    );
};

export default TrackEventScraperDialog;
